/**
 * Watching a section, and offering to repeat what happened in it.
 *
 * A "visit" starts the first time a click lands inside a section and ends when
 * one lands outside it — which is what "when we click off the material, work
 * order, or other expenses" means in a card where every edit saves itself and
 * there is no Save button to hang the question on. The two order editors are
 * simpler: they are modals, so the visit is the time they are open.
 *
 * What is compared is the section before against the section after, not a log
 * of keystrokes. A quantity typed wrong and typed back, or an edit taken back
 * with Undo, leaves nothing behind and raises nothing.
 */

import {
  actionIsLive,
  diffRows,
  estimateLabel,
  mirrorOffered,
  mirrorTargets,
  type Estimate,
  type Job,
  type MirrorBucket,
  type MirrorChange,
  type MirrorRow,
} from "@roof-hawk/core";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { t } from "../../i18n";
import { fetchEstimateChildren } from "../../lib/queries";
import { MirrorPrompt } from "./MirrorPrompt";
import {
  applyMirrorPlan,
  buildMirrorPlans,
  mirrorRowsOf,
  rawById,
  type EstimateChildren,
  type MirrorPlan,
} from "./mirror";

/**
 * A section of the estimate card, for the purposes of "have they left it yet".
 *
 * `display: contents` so that wrapping a section changes nothing about how it
 * is laid out — the card's blocks are siblings and its CSS says so.
 */
export function MirrorSection({
  onEnter,
  onLeave,
  children,
}: {
  onEnter: () => void;
  onLeave: () => void;
  children: ReactNode;
}) {
  const box = useRef<HTMLDivElement | null>(null);
  const inside = useRef(false);
  const handlers = useRef({ onEnter, onLeave });
  handlers.current = { onEnter, onLeave };

  useEffect(() => {
    const down = (event: PointerEvent) => {
      const element = box.current;
      if (!element || !(event.target instanceof Node)) return;

      if (element.contains(event.target)) {
        if (!inside.current) {
          inside.current = true;
          handlers.current.onEnter();
        }
        return;
      }

      // A click inside a modal is not leaving the section. Every one of them is
      // a portal into <body>, so without this, opening a line item to edit it
      // would count as walking away from the estimate.
      if (
        event.target instanceof Element &&
        event.target.closest(".modal-bg")
      ) {
        return;
      }

      if (inside.current) {
        inside.current = false;
        handlers.current.onLeave();
      }
    };

    // Capture, so the snapshot is taken before the click that starts the edit.
    document.addEventListener("pointerdown", down, true);
    return () => document.removeEventListener("pointerdown", down, true);
  }, []);

  return (
    <div style={{ display: "contents" }} ref={box}>
      {children}
    </div>
  );
}

/** A visit in progress: what the section looked like when it started. */
interface Visit {
  bucket: MirrorBucket;
  containerId: string | null;
  estimateId: string;
  before: MirrorRow[];
}

interface Pending {
  bucket: MirrorBucket;
  containerId: string | null;
  changes: MirrorChange[];
  plans: MirrorPlan[];
  sourceRaw: Map<string, Record<string, unknown>>;
}

export function useMirror({
  job,
  estimate,
  estimates,
  children,
  onApplied,
}: {
  job: Job;
  estimate: Estimate | null;
  estimates: readonly Estimate[];
  children: EstimateChildren | null;
  /** Reload the card once rows on other estimates have been written. */
  onApplied: () => Promise<void>;
}) {
  const visits = useRef(new Map<string, Visit>());
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const key = (bucket: MirrorBucket, containerId: string | null) =>
    `${bucket}|${containerId ?? ""}`;

  const begin = useCallback(
    (bucket: MirrorBucket, containerId: string | null = null) => {
      if (!estimate || !children) return;
      visits.current.set(key(bucket, containerId), {
        bucket,
        containerId,
        estimateId: estimate.id,
        before: mirrorRowsOf(bucket, children, containerId),
      });
    },
    [estimate, children],
  );

  const end = useCallback(
    (bucket: MirrorBucket, containerId: string | null = null) => {
      const visit = visits.current.get(key(bucket, containerId));
      visits.current.delete(key(bucket, containerId));
      if (!visit || !estimate) return;
      // Switching estimates mid-visit: the snapshot describes a different
      // estimate and comparing it to this one would invent changes.
      if (visit.estimateId !== estimate.id) return;

      void (async () => {
        try {
          const fresh = await fetchEstimateChildren(visit.estimateId);

          // The order was deleted from under the visit. Everything on it looks
          // removed, and none of it is a change anybody asked to repeat.
          if (visit.containerId) {
            const orders =
              bucket === "material" ? fresh.materialOrders : fresh.workOrders;
            if (!orders.some((o) => o.id === visit.containerId)) return;
          }

          const after = mirrorRowsOf(bucket, fresh, visit.containerId);
          const changes = diffRows(visit.before, after);
          const candidates = mirrorTargets({
            job,
            estimates,
            sourceId: visit.estimateId,
          });
          if (!mirrorOffered({ changes, candidates })) return;

          const loaded = await Promise.all(
            candidates.map(
              async (candidate) =>
                [
                  candidate.estimate.id,
                  await fetchEstimateChildren(candidate.estimate.id),
                ] as const,
            ),
          );

          const plans = buildMirrorPlans({
            bucket,
            changes,
            candidates,
            source: fresh,
            sourceContainerId: visit.containerId,
            childrenById: new Map(loaded),
          });

          // Nothing to offer: every other estimate either already matches or
          // cannot take the change. Saying so would be a dialog whose only
          // button is "fine".
          if (!plans.some((plan) => plan.actions.some(actionIsLive))) return;

          setError(null);
          setPending({
            bucket,
            containerId: visit.containerId,
            changes,
            plans,
            sourceRaw: rawById(bucket, fresh, visit.containerId),
          });
        } catch (problem) {
          // A failure to *offer* the mirror must not break the card. The edit
          // itself has already been saved.
          setError(
            problem instanceof Error ? problem.message : String(problem),
          );
        }
      })();
    },
    [estimate, estimates, job],
  );

  const apply = useCallback(
    ({
      estimateIds,
      asks,
    }: {
      estimateIds: ReadonlySet<string>;
      asks: ReadonlySet<string>;
    }) => {
      if (!pending) return;
      setBusy(true);
      setError(null);
      void (async () => {
        try {
          for (const plan of pending.plans) {
            if (plan.blocked) continue;
            if (!estimateIds.has(plan.candidate.estimate.id)) continue;
            await applyMirrorPlan({
              bucket: pending.bucket,
              plan,
              sourceRawById: pending.sourceRaw,
              jobId: job.id,
              asks,
            });
          }
          await onApplied();
          setPending(null);
        } catch (problem) {
          setError(
            problem instanceof Error ? problem.message : String(problem),
          );
        } finally {
          setBusy(false);
        }
      })();
    },
    [pending, job.id, onApplied],
  );

  const node = pending ? (
    <MirrorPrompt
      bucket={pending.bucket}
      sourceLabel={estimate ? estimateLabel(estimate) : t("this estimate")}
      changes={pending.changes}
      plans={pending.plans}
      busy={busy}
      error={error}
      onClose={() => {
        if (busy) return;
        setPending(null);
        setError(null);
      }}
      onApply={apply}
    />
  ) : null;

  return { begin, end, node };
}
